import { useState, useRef } from 'react';
import type { Site, Page, Component, LibraryComponent, PageLayout } from '../../schemas/validation';

export interface PageEditorState {
  site: Site | null;
  setSite: React.Dispatch<React.SetStateAction<Site | null>>;
  currentPage: Page | null;
  setCurrentPage: React.Dispatch<React.SetStateAction<Page | null>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  showSaveTemplateModal: boolean;
  setShowSaveTemplateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showComponentLibraryModal: boolean;
  setShowComponentLibraryModal: React.Dispatch<React.SetStateAction<boolean>>;
  isInvisibleComponentMode: boolean;
  setIsInvisibleComponentMode: React.Dispatch<React.SetStateAction<boolean>>;
  showHeadContentModal: boolean;
  setShowHeadContentModal: React.Dispatch<React.SetStateAction<boolean>>;
  showBodyEndModal: boolean;
  setShowBodyEndModal: React.Dispatch<React.SetStateAction<boolean>>;
  showEditPageModal: boolean;
  setShowEditPageModal: React.Dispatch<React.SetStateAction<boolean>>;
  isSavingTemplate: boolean;
  setIsSavingTemplate: React.Dispatch<React.SetStateAction<boolean>>;
  isAddingComponent: boolean;
  setIsAddingComponent: React.Dispatch<React.SetStateAction<boolean>>;
  showSuccessToast: boolean;
  setShowSuccessToast: React.Dispatch<React.SetStateAction<boolean>>;
  successMessage: string;
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>;
  componentRefreshTrigger: number;
  setComponentRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  components: Component[];
  setComponents: React.Dispatch<React.SetStateAction<Component[]>>;
  editingInvisibleComponent: Component | null;
  setEditingInvisibleComponent: React.Dispatch<React.SetStateAction<Component | null>>;
  themeColors: any;
  setThemeColors: React.Dispatch<React.SetStateAction<any>>;
  isLoadingTheme: boolean;
  setIsLoadingTheme: React.Dispatch<React.SetStateAction<boolean>>;
  themeError: string | null;
  setThemeError: React.Dispatch<React.SetStateAction<string | null>>;
  currentThemeName: string | null;
  setCurrentThemeName: React.Dispatch<React.SetStateAction<string | null>>;
  showResponsivePreview: boolean;
  setShowResponsivePreview: React.Dispatch<React.SetStateAction<boolean>>;
  sitePages: Page[];
  setSitePages: React.Dispatch<React.SetStateAction<Page[]>>;
  isPageDropdownOpen: boolean;
  setIsPageDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  insertPosition: number | null;
  setInsertPosition: React.Dispatch<React.SetStateAction<number | null>>;
  showSEOAnalysis: boolean;
  setShowSEOAnalysis: React.Dispatch<React.SetStateAction<boolean>>;
  pageLayout: PageLayout | null;
  setPageLayout: React.Dispatch<React.SetStateAction<PageLayout | null>>;
  duplicateModal: { isOpen: boolean; type: string; comps: Component[] };
  setDuplicateModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; type: string; comps: Component[] }>
  >;
  deleteModal: { isOpen: boolean; component: Component | null };
  setDeleteModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; component: Component | null }>
  >;
  libraryEditorModal: { isOpen: boolean; component: LibraryComponent | null };
  setLibraryEditorModal: React.Dispatch<
    React.SetStateAction<{ isOpen: boolean; component: LibraryComponent | null }>
  >;
  isPublishing: boolean;
  setIsPublishing: React.Dispatch<React.SetStateAction<boolean>>;
  pageDropdownRef: React.RefObject<HTMLDivElement | null>;
}

export const usePageEditorState = (): PageEditorState => {
  const [site, setSite] = useState<Site | null>(null);
  const [currentPage, setCurrentPage] = useState<Page | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showComponentLibraryModal, setShowComponentLibraryModal] = useState(false);
  const [isInvisibleComponentMode, setIsInvisibleComponentMode] = useState(false);
  const [showHeadContentModal, setShowHeadContentModal] = useState(false);
  const [showBodyEndModal, setShowBodyEndModal] = useState(false);
  const [showEditPageModal, setShowEditPageModal] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isAddingComponent, setIsAddingComponent] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [componentRefreshTrigger, setComponentRefreshTrigger] = useState(0);
  const [components, setComponents] = useState<Component[]>([]);
  const [editingInvisibleComponent, setEditingInvisibleComponent] = useState<Component | null>(
    null
  );
  const [themeColors, setThemeColors] = useState<any>(null);
  const [isLoadingTheme, setIsLoadingTheme] = useState(false);
  const [themeError, setThemeError] = useState<string | null>(null);
  const [currentThemeName, setCurrentThemeName] = useState<string | null>(null);
  const [showResponsivePreview, setShowResponsivePreview] = useState(false);
  const [sitePages, setSitePages] = useState<Page[]>([]);
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [insertPosition, setInsertPosition] = useState<number | null>(null);
  const [showSEOAnalysis, setShowSEOAnalysis] = useState(false);
  const [pageLayout, setPageLayout] = useState<PageLayout | null>(null);

  // Modal states
  const [duplicateModal, setDuplicateModal] = useState<{
    isOpen: boolean;
    type: string;
    comps: Component[];
  }>({ isOpen: false, type: '', comps: [] });

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    component: Component | null;
  }>({ isOpen: false, component: null });

  const [libraryEditorModal, setLibraryEditorModal] = useState<{
    isOpen: boolean;
    component: LibraryComponent | null;
  }>({ isOpen: false, component: null });

  const [isPublishing, setIsPublishing] = useState(false);

  // Ref for page dropdown click-outside detection
  const pageDropdownRef = useRef<HTMLDivElement>(null);

  return {
    site,
    setSite,
    currentPage,
    setCurrentPage,
    isLoading,
    setIsLoading,
    showSaveTemplateModal,
    setShowSaveTemplateModal,
    showComponentLibraryModal,
    setShowComponentLibraryModal,
    isInvisibleComponentMode,
    setIsInvisibleComponentMode,
    showHeadContentModal,
    setShowHeadContentModal,
    showBodyEndModal,
    setShowBodyEndModal,
    showEditPageModal,
    setShowEditPageModal,
    isSavingTemplate,
    setIsSavingTemplate,
    isAddingComponent,
    setIsAddingComponent,
    showSuccessToast,
    setShowSuccessToast,
    successMessage,
    setSuccessMessage,
    componentRefreshTrigger,
    setComponentRefreshTrigger,
    components,
    setComponents,
    editingInvisibleComponent,
    setEditingInvisibleComponent,
    themeColors,
    setThemeColors,
    isLoadingTheme,
    setIsLoadingTheme,
    themeError,
    setThemeError,
    currentThemeName,
    setCurrentThemeName,
    showResponsivePreview,
    setShowResponsivePreview,
    sitePages,
    setSitePages,
    isPageDropdownOpen,
    setIsPageDropdownOpen,
    insertPosition,
    setInsertPosition,
    showSEOAnalysis,
    setShowSEOAnalysis,
    pageLayout,
    setPageLayout,
    duplicateModal,
    setDuplicateModal,
    deleteModal,
    setDeleteModal,
    libraryEditorModal,
    setLibraryEditorModal,
    isPublishing,
    setIsPublishing,
    pageDropdownRef,
  };
};
